/* Loading a book someone else wrote out.
 *
 * Typing eleven positions across five brokers is the reason a book never gets
 * started. §1 rules out reading them from the brokerage, so the way round is a
 * file: photograph the position screens, hand the pictures and the instructions
 * below to whatever assistant you like, and load what comes back.
 *
 * Everything that arrives this way was read off a picture by something that can
 * misread a digit and will not mention it. So the file is checked field by
 * field, refused whole if any part of it is wrong, and shown in full before a
 * single row is written. */

import { useRef, useState } from 'react';
import { parseStarter, STARTER_FORMAT, type StarterResult } from '../state/starter';
import { useStore } from '../state/store';

const INSTRUCTIONS = `I am filling in a starting file for an app called Position Book.
I will give you screenshots of my brokerage and exchange accounts. Read them and
reply with ONE JSON file and nothing else. No commentary, no markdown fence.

Rules that matter more than filling every field:

- Never guess a number. If a figure is not legible, leave that field out. A
  missing average cost is fine and the app handles it; a wrong one quietly
  corrupts every return it is used in.
- Do not convert currencies. Write each figure in the currency shown on screen.
- Keep every decimal of a share count. 208.6132 is not 209.
- For a futures contract give "marginUsdt" and "leverage" only. Do NOT copy the
  "Size (USDT)" figure from the position card, which is contracts times the
  current mark and drifts every tick. The app derives the notional itself.
- Dates as YYYY-MM-DD.
- Every "account" must match a "name" in the accounts list exactly.
- Do not invent a thesis, a reason to own something, or a price target. Those
  are mine to write, and the app deliberately leaves them blank until I do.

The shape, with every optional field marked:

{
  "format": "${STARTER_FORMAT}",
  "version": 1,
  "accounts": [
    { "name": "Revolut", "currency": "USD",
      "taxShielded": false,      // optional — true for a tax-sheltered account
      "isFutures": false }       // optional — true for a futures venue
  ],
  "holdings": [
    { "account": "Revolut", "ticker": "ZETA", "shares": 668,
      "avgCost": 17.12,          // optional — leave out if not on the screen
      "currency": "USD",         // optional — defaults to the broker's
      "yahooSymbol": "ZETA",     // optional — needed for non-US: 2498.HK, EL.PA
      "openedAt": "2026-06-03" } // optional
  ],
  "cash": [
    { "account": "Revolut", "amount": 6.74, "currency": "USD" }
  ],
  "perps": [                     // optional — leveraged contracts
    { "symbol": "MUSTOCK_USDT",  // the API symbol, not the display name
      "display": "MU", "side": "long", "leverage": 5,
      "marginUsdt": 1035.7765, "entryPrice": 839.36, "liqPrice": 674.84,
      "takeProfit": 1420 }       // optional
  ],
  "cashFlows": [                 // optional — deposits and withdrawals
    { "account": "Revolut", "amount": 5000, "currency": "USD", "date": "2026-05-02" },
    // "account" is optional: leave it out for money paid into the book as a
    // whole. Do not pick a broker to fill the field — an unnamed account is
    // the true answer when the split is not on the screenshots.
    { "amount": 42000, "currency": "USD", "date": "2024-01-15" }
  ],
  "themes": [                    // optional
    { "name": "AI capex", "tickers": ["ZETA", "HSAI"] }
  ],
  "valueTimeline": [             // optional — account values you know from before
    { "date": "2026-06-03", "totalUsd": 58040 }
  ]
}

If a screenshot is unreadable, say which one rather than filling it in.`;

export function StarterImport({ say }: { say: (message: string) => void }) {
  const { book, replaceBook } = useStore();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [result, setResult] = useState<StarterResult | null>(null);
  const [fileName, setFileName] = useState('');
  const [showText, setShowText] = useState(false);
  const [busy, setBusy] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(INSTRUCTIONS);
      say('Instructions copied. Paste them into an assistant with your screenshots.');
    } catch {
      setShowText(true);
      say('Clipboard is blocked here. The text is below, select and copy it.');
    }
  };

  const pick = async (file: File) => {
    setFileName(file.name);
    setResult(parseStarter(await file.text()));
  };

  const load = async () => {
    if (!result?.book) return;
    setBusy(true);
    try {
      await replaceBook(result.book);
      say(`Loaded ${result.summary.holdings} positions from ${fileName}.`);
      setResult(null);
      setFileName('');
    } finally {
      setBusy(false);
    }
  };

  const s = result?.summary;

  return (
    <>
      <div className="dim" style={{ fontSize: 11.5, marginBottom: 12, lineHeight: 1.5 }}>
        Rather than typing a book in, photograph your position screens, hand the pictures and these
        instructions to an assistant, and load the file it writes. Nothing about your account leaves
        this device by doing so. You choose what is in the pictures, and the file arrives back the
        same way.
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="btn btn-sm" onClick={() => void copy()}>Copy the instructions</button>
        <button className="btn btn-ghost btn-sm" onClick={() => setShowText((v) => !v)}>
          {showText ? 'Hide them' : 'Show them'}
        </button>
      </div>

      {showText ? (
        <textarea className="input" readOnly rows={10} value={INSTRUCTIONS}
                  aria-label="Instructions to hand to an assistant"
                  style={{ marginTop: 10, fontSize: 11, lineHeight: 1.45 }} />
      ) : null}

      <input
        ref={fileRef} type="file" accept=".json,application/json" style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) void pick(f);
        }}
      />

      <button className="btn btn-block" style={{ marginTop: 12 }}
              onClick={() => fileRef.current?.click()}>
        Load a starting file
      </button>

      {result ? (
        <div className="card card-pad" style={{ marginTop: 12 }}>
          <div className="dim" style={{ fontSize: 11.5, marginBottom: 10 }}>{fileName}</div>

          {result.book && s ? (
            <>
              <div className="kv"><span className="k">Brokers</span><span className="v num">{s.accounts}</span></div>
              <div className="kv"><span className="k">Positions</span><span className="v num">{s.holdings}</span></div>
              {s.perps > 0 ? <div className="kv"><span className="k">Contracts</span><span className="v num">{s.perps}</span></div> : null}
              {s.cash > 0 ? <div className="kv"><span className="k">Cash balances</span><span className="v num">{s.cash}</span></div> : null}
              {s.cashFlows > 0 ? <div className="kv"><span className="k">Deposits and withdrawals</span><span className="v num">{s.cashFlows}</span></div> : null}
              {s.themes > 0 ? <div className="kv"><span className="k">Themes</span><span className="v num">{s.themes}</span></div> : null}
              {s.timeline > 0 ? <div className="kv"><span className="k">Past values</span><span className="v num">{s.timeline}</span></div> : null}

              {s.withoutCost > 0 ? (
                <div className="dim" style={{ fontSize: 11, marginTop: 8, lineHeight: 1.45 }}>
                  {s.withoutCost} position{s.withoutCost === 1 ? '' : 's'} came without an average
                  cost. They load, and their return stays blank until you fill it in, because an unknown
                  is kept as one rather than read as a free position.
                </div>
              ) : null}

              <div className="warn" style={{ fontSize: 11.5, marginTop: 10, lineHeight: 1.45 }}>
                This replaces everything currently in the book: {book.positions.length} position
                {book.positions.length === 1 ? '' : 's'} and what you have written about them.
                Export to a file first if any of it matters.
              </div>

              <div className="dim" style={{ fontSize: 11, marginTop: 8, lineHeight: 1.45 }}>
                Check these counts against your screenshots before loading. Everything here was read
                off a picture by something that cannot tell you when it misread one.
              </div>
            </>
          ) : (
            <>
              <div className="warn" style={{ fontSize: 12, marginBottom: 8 }}>
                Nothing was loaded. {result.problems.length} thing
                {result.problems.length === 1 ? '' : 's'} to fix:
              </div>
              <div className="dim" style={{ fontSize: 11, lineHeight: 1.6 }}>
                {result.problems.slice(0, 12).map((p, i) => (
                  <div key={i}><span className="num">{p.path}</span>: {p.why}</div>
                ))}
                {result.problems.length > 12
                  ? <div>…and {result.problems.length - 12} more</div> : null}
              </div>
            </>
          )}

          <div className="fab-row" style={{ marginTop: 12 }}>
            <button className="btn btn-block" onClick={() => { setResult(null); setFileName(''); }}>
              Cancel
            </button>
            {result.book ? (
              <button className="btn btn-primary btn-block" disabled={busy} onClick={() => void load()}>
                {busy ? 'Loading…' : 'Replace the book'}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
}
